import React from "react";
import { Container, Breadcrumb, ListGroup } from "react-bootstrap";
import { Link, useParams, Navigate } from "react-router-dom";
import styled from "styled-components";
import { FaHome, FaChevronRight } from "react-icons/fa";
import { articles } from "../data/helpArticles";
import { getRelativeTimeString } from "../utils/dateUtils";
import ContactSupport from "../components/ContactSupport";

const StyledContainer = styled(Container)`
  max-width: 800px;
  padding: 2rem 1rem;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 2rem;

  .breadcrumb {
    margin-bottom: 0;
  }

  a {
    color: var(--primary-bg);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const CategoryHeader = styled.div`
  margin-bottom: 2rem;
`;

const StyledListGroup = styled(ListGroup)`
  margin-bottom: 3rem;

  .list-group-item {
    border-left: none;
    border-right: none;
    padding: 1rem 0;

    &:first-child {
      border-top: none;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;

const ArticleLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: inherit;
  text-decoration: none;
  padding: 0.5rem 0;

  &:hover {
    color: var(--primary-bg);
    text-decoration: none;

    .article-title {
      color: var(--primary-bg);
    }
  }
`;

const ArticleTitle = styled.span`
  font-weight: 500;
`;

const LastUpdated = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const HelpCategory: React.FC = () => {
  const { category } = useParams<{ category: string }>();

  if (!category || !articles[category]) {
    return <Navigate to="/help" replace />;
  }

  const categoryArticles = Object.values(articles[category]);
  const categoryName = category.charAt(0).toUpperCase() + category.slice(1);

  return (
    <StyledContainer>
      <StyledBreadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <FaHome />
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/help" }}>
          Help Center
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{categoryName}</Breadcrumb.Item>
      </StyledBreadcrumb>

      <CategoryHeader>
        <h1>{categoryName}</h1>
        <p className="text-muted">
          Browse through {categoryArticles.length} articles in this category
        </p>
      </CategoryHeader>

      <StyledListGroup>
        {categoryArticles.map((article) => (
          <ListGroup.Item key={article.slug}>
            <ArticleLink to={`/help/${category}/${article.slug}`}>
              <div>
                <ArticleTitle className="article-title">
                  {article.title}
                </ArticleTitle>
              </div>
              <div className="d-flex align-items-center">
                <LastUpdated>
                  Updated {getRelativeTimeString(article.lastUpdated)}
                </LastUpdated>
                <FaChevronRight className="ms-3" />
              </div>
            </ArticleLink>
          </ListGroup.Item>
        ))}
      </StyledListGroup>

      <ContactSupport />
    </StyledContainer>
  );
};

export default HelpCategory;
